import React, { useState } from 'react';
import { CheckCircle, FileText, Clock, Calendar, User, MessageSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Badge, Input, Alert } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const TaskCustom = ({ task, taskType, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      // Mark task as completed
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session?.sessionId,
          taskId: task.taskId,
          completionNotes: completionNotes.trim() || null
        },
        user,
        userInfo?.userId
      );

      // Analytics tracking is now handled automatically in TaskPage
      // Pass additional properties if needed
      toast.success('Task completed successfully!');
      onComplete({ hasCompletionNotes: !!completionNotes.trim() });
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3 md:px-0">
      <Card variant="elevated" padding="large">
        {/* Task Header */}
        <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 mb-4 md:mb-6">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              {task.title}
            </h1>
            <p className="text-sm md:text-base text-gray-600 mb-4">
              {task.description}
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                Created {new Date(task.createdAt).toLocaleDateString()}
              </div>
              {task.dueDate && (
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Due {new Date(task.dueDate).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Task Instructions */}
        <Alert variant="info" className="mb-4 md:mb-6">
          <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">Instructions</h3>
          <p className="text-sm md:text-base text-gray-700">
            Please review the task details above and complete the assigned work. 
            Once you've finished, you can mark this task as complete below.
          </p>
        </Alert>

        {/* Completion Notes */}
        <div className="mb-4 md:mb-6">
          <label htmlFor="completionNotes" className="block text-sm font-medium text-gray-700 mb-2">
            Completion Notes (Optional)
          </label>
          <Input
            id="completionNotes"
            type="textarea"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            rows={4}
            placeholder="Add any notes about your completion of this task..."
          />
        </div>

        {/* Complete Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleComplete}
            disabled={isSubmitting}
            variant="primary"
            size="medium"
            className="flex items-center"
          >
            <CheckCircle className="h-4 w-4 md:h-5 md:w-5 mr-2" />
            {isSubmitting ? 'Completing...' : 'Mark as Complete'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Add static methods to the component for Summary and ResultsSummary
TaskCustom.Summary = ({ task, session }) => {
  if (!session?.completionNotes) return null;

  return (
    <Card variant="default" padding="medium">
      <div className="flex items-center space-x-2 mb-2">
        <FileText className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-900">Custom Task</span>
      </div>
      <p className="text-xs text-gray-600 line-clamp-2">
        {session.completionNotes}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        Completed {new Date(session.completedAt || Date.now()).toLocaleDateString()}
      </p>
    </Card>
  );
};

TaskCustom.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData?.completionNotes) return null;

  return (
    <Card variant="elevated" padding="large">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">Custom Task Results</h4>
      
      <div className="flex flex-col md:flex-row items-start space-y-1 md:space-y-0 md:space-x-2 mb-1">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-indigo-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="text-lg font-medium text-gray-900">{task.title}</h5>
          <p className="text-sm text-gray-500">
            Completed by {patientName} on {new Date(completionData.completedAt || Date.now()).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="border-t pt-1">
        <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
          <MessageSquare className="h-4 w-4 mr-1" />
          Completion Notes:
        </h6>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600 whitespace-pre-wrap">
            {completionData.completionNotes}
          </p>
        </div>
      </div>
    </Card>
  );
};

// Add static method for task assignment form
TaskCustom.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  return (
    <Card variant="default" padding="large">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task Title *
          </label>
          <Input
            type="text"
            value={formData.title || ''}
            onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
            placeholder="Enter task title..."
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task Description *
          </label>
          <Input
            type="textarea"
            value={formData.description || ''}
            onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
            placeholder="Enter task description..."
            rows={3}
            required
          />
        </div>
      </div>
    </Card>
  );
};

export default TaskCustom; 